/**
 * App root — React Router v7 setup.
 *
 * - ToastProvider wraps everything for global toast access.
 * - CommandPalette mounts globally (Cmd+K to open from anywhere).
 * - RouteProgress shows a top bar during navigation (click feedback).
 * - CookieBanner appears on first visit for strictly-necessary consent.
 * - ErrorBoundary catches per-route errors with a friendly fallback.
 * - Each page module is lazy-loaded via `lib/routePrefetch` so hover
 *   prefetch and idle prefetch reuse the same memoised promises.
 */

import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

import AppLayout       from "@/primitives/layout/AppLayout";
import ProtectedRoute  from "@/primitives/layout/ProtectedRoute";
import LoadingFallback from "@/primitives/layout/LoadingFallback";
import { ToastProvider } from "@/components/Toast";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CommandPalette } from "@/components/CommandPalette";
import RouteProgress from "@/components/RouteProgress";
import CookieBanner from "@/components/CookieBanner";
import SplashOverlay from "@/components/SplashOverlay";
import OutOfCreditsModal from "@/components/OutOfCreditsModal";
import { listenForSwMessages } from "@/lib/pwa";
import { lazyRoute } from "@/lib/routePrefetch";
import { ROUTES } from "@/types";

// Public
const LoginPage       = lazy(lazyRoute(ROUTES.LOGIN));
const SignupPage      = lazy(() => import("@/pages/auth/SignupPage"));
const PricingPage     = lazy(lazyRoute(ROUTES.PRICING));
const TermsOfService  = lazy(lazyRoute(ROUTES.TERMS));
const PrivacyPolicy   = lazy(lazyRoute(ROUTES.PRIVACY));
const CookiePolicy    = lazy(lazyRoute(ROUTES.COOKIES));
const RefundPolicy    = lazy(lazyRoute(ROUTES.REFUND));

// Protected
const DashboardPage   = lazy(lazyRoute(ROUTES.DASHBOARD));
// StudioPage is no longer imported — /studio 301-redirects to /dashboard.
// Phase 0: Dashboard PromptDeck is the only generation surface.
const LibraryPage     = lazy(lazyRoute(ROUTES.LIBRARY));
const EditorPage      = lazy(lazyRoute(ROUTES.EDITOR));
const CalendarPage    = lazy(lazyRoute(ROUTES.CALENDAR));
const BrandKitPage    = lazy(lazyRoute(ROUTES.BRAND_KIT));
const TemplatesPage   = lazy(lazyRoute(ROUTES.TEMPLATES));
const InspirationPage = lazy(lazyRoute(ROUTES.INSPIRATION));
const WorkspacesPage  = lazy(lazyRoute(ROUTES.WORKSPACES));
const SettingsPage    = lazy(lazyRoute(ROUTES.SETTINGS));
const TeamPage        = lazy(lazyRoute(ROUTES.TEAM));
const PlansPage       = lazy(lazyRoute(ROUTES.PLANS));
const AdminPage       = lazy(lazyRoute(ROUTES.ADMIN));
const OnboardingWorkspacePage = lazy(() => import("@/pages/onboarding/OnboardingWorkspacePage"));

function SwBridge() {
  const navigate = useNavigate();
  useEffect(() => { listenForSwMessages((url) => navigate(url)); }, [navigate]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <SplashOverlay />
        <SwBridge />
        <RouteProgress />
        <CommandPalette />
        <CookieBanner />
        <OutOfCreditsModal />
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public */}
              <Route path={ROUTES.LOGIN}   element={<LoginPage />} />
              <Route path={ROUTES.SIGNUP}  element={<SignupPage />} />
              <Route path={ROUTES.PRICING} element={<PricingPage />} />
              <Route path={ROUTES.TERMS}   element={<TermsOfService />} />
              <Route path={ROUTES.PRIVACY} element={<PrivacyPolicy />} />
              <Route path={ROUTES.COOKIES} element={<CookiePolicy />} />
              <Route path={ROUTES.REFUND}  element={<RefundPolicy />} />

              {/* Protected — wrapped in TopNav layout */}
              <Route element={<ProtectedRoute />}>
                {/* Onboarding lives OUTSIDE AppLayout so it doesn't render
                    TopNav (new users haven't picked a workspace yet — the
                    nav's workspace switcher would crash). */}
                <Route path={ROUTES.ONBOARDING_WORKSPACE} element={<OnboardingWorkspacePage />} />

                <Route element={<AppLayout />}>
                  <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                  <Route path={ROUTES.DASHBOARD}   element={<DashboardPage />} />
                  {/* Legacy bookmark compatibility — keep /dashboard2 routed
                      at the same component so shared links don't 404. */}
                  <Route path="/dashboard2"        element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                  {/* Phase 0: /studio → /dashboard. Studio page retired;
                      Dashboard PromptDeck is the only generation surface. */}
                  <Route path={ROUTES.STUDIO}      element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                  <Route path={ROUTES.LIBRARY}     element={<LibraryPage />} />
                  <Route path={ROUTES.EDITOR}      element={<EditorPage />} />
                  <Route path={ROUTES.CALENDAR}    element={<CalendarPage />} />
                  <Route path={ROUTES.BRAND_KIT}   element={<BrandKitPage />} />
                  <Route path={ROUTES.TEMPLATES}   element={<TemplatesPage />} />
                  <Route path={ROUTES.INSPIRATION} element={<InspirationPage />} />
                  <Route path={ROUTES.WORKSPACES}  element={<WorkspacesPage />} />
                  <Route path={ROUTES.SETTINGS}    element={<SettingsPage />} />
                  <Route path={ROUTES.TEAM}        element={<TeamPage />} />
                  <Route path={ROUTES.PLANS}       element={<PlansPage />} />
                  <Route path={ROUTES.ADMIN}       element={<AdminPage />} />
                </Route>
              </Route>

              {/* 404 */}
              <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </ToastProvider>
    </BrowserRouter>
  );
}
